import {
  revenuePolyByMarkets,
  revenuePrintByMarkets,
  revenuePSByMarkets,
  TRevenueSnapshotKeys,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFinancialData'
import { IRevenue } from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getPostfixByActualsValueType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPostfixByActualsValueType'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getPercentDiff } from '../../../helpers/getPercentDiff'

import { getFixedSum } from './getFixedSum'
import { getRevenueItemName } from './getRevenueItemName'

interface IRecomputeGeneralTotalPayload {
  key: TRevenueSnapshotKeys
  psRevenue: IRevenue
  printRevenue: IRevenue
  actualValueTypes: ActualsValueTypes[]
}

const revenueValueFields = [
  'CurrentYearRevenue',
  'PreviousYearRevenue',
  'CurrentYearQuarter1',
  'CurrentYearQuarter2',
  'CurrentYearQuarter3',
  'CurrentYearQuarter4',
  'PrevYearQuarter1',
  'PrevYearQuarter2',
  'PrevYearQuarter3',
  'PrevYearQuarter4',
  'TwoYearsAgo',
  'ThreeYearsAgo',
]

const getNumberField = (item: IRevenue, field: string): number => {
  return (item[field as keyof IRevenue] as number | null | undefined) ?? 0
}

const getRevenueFieldsSum = (
  firstItem: IRevenue,
  secondItem: IRevenue,
  actualValueTypes: ActualsValueTypes[]
): Partial<IRevenue> => {
  return actualValueTypes.reduce<Partial<IRevenue>>((acc, valueType) => {
    const postfix = getPostfixByActualsValueType(valueType)

    const fields = revenueValueFields.reduce<Partial<IRevenue>>((fieldAcc, field) => {
      const fieldKey = `${field}${postfix}` as keyof IRevenue

      return {
        ...fieldAcc,
        [fieldKey]: Number(
          (getNumberField(firstItem, fieldKey) + getNumberField(secondItem, fieldKey)).toFixed(2)
        ),
      }
    }, {})

    const currentYear = getNumberField(fields as IRevenue, `CurrentYearRevenue${postfix}`)
    const previousYear = getNumberField(fields as IRevenue, `PreviousYearRevenue${postfix}`)

    return {
      ...acc,
      ...fields,
      [`YoY${postfix}`]: getPercentDiff(currentYear, previousYear),
    }
  }, {})
}

const recomputeGeneralTotal = ({
  key,
  psRevenue,
  printRevenue,
  actualValueTypes,
}: IRecomputeGeneralTotalPayload): IRevenue => {
  return {
    Key: key,
    Name: getRevenueItemName(key),
    ...getRevenueFieldsSum(psRevenue, printRevenue, actualValueTypes),
  } as IRevenue
}

const recomputeRowTotal =
  (key: TRevenueSnapshotKeys, data: IRevenue[], actualValueTypes: ActualsValueTypes[]) =>
  (previousValue: IRevenue, currentValue: string) => {
    const item = data.find((el) => el.Key === currentValue)

    if (!item) {
      return previousValue
    }

    const summedFields = actualValueTypes.reduce<Partial<IRevenue>>((acc, valueType) => {
      const postfix = getPostfixByActualsValueType(valueType)

      const fields = revenueValueFields.reduce<Partial<IRevenue>>((fieldAcc, field) => {
        const fieldKey = `${field}${postfix}` as keyof IRevenue

        return {
          ...fieldAcc,
          [fieldKey]: getFixedSum(
            getNumberField(previousValue, fieldKey),
            getNumberField(item, fieldKey)
          ),
        }
      }, {})

      const currentYear = getNumberField(fields as IRevenue, `CurrentYearRevenue${postfix}`)
      const previousYear = getNumberField(fields as IRevenue, `PreviousYearRevenue${postfix}`)

      return {
        ...acc,
        ...fields,
        [`YoY${postfix}`]: getPercentDiff(currentYear, previousYear),
      }
    }, {})

    return {
      Key: key,
      Name: getRevenueItemName(key),
      ...summedFields,
    } as IRevenue
  }

export const recomputeRowsTotal = (data: IRevenue[], actualValueTypes: ActualsValueTypes[]) => {
  const psTotalByMarkets = revenuePSByMarkets.reduce(
    recomputeRowTotal('PersonalSystemsByMarketsTotal', data, actualValueTypes),
    {} as IRevenue
  )

  const printTotalByMarkets = revenuePrintByMarkets.reduce(
    recomputeRowTotal('PrintByMarketsTotal', data, actualValueTypes),
    {} as IRevenue
  )

  const polyByMarketsTotal = revenuePolyByMarkets.reduce(
    recomputeRowTotal('PolyByMarketsTotal', data, actualValueTypes),
    {} as IRevenue
  )

  const grandTotalByMarkets = recomputeGeneralTotal({
    key: 'GrandTotal',
    printRevenue: printTotalByMarkets,
    psRevenue: psTotalByMarkets,
    actualValueTypes,
  })

  const indexPsTotalByMarkets = data.findIndex((el) => el.Key === 'PersonalSystemsByMarketsTotal')
  const indexPrintTotalByMarkets = data.findIndex((el) => el.Key === 'PrintByMarketsTotal')
  const indexPolyTotalByMarkets = data.findIndex((el) => el.Key === 'PolyByMarketsTotal')
  const indexGrandTotalByMarkets = data.findIndex((el) => el.Key === 'GrandTotal')

  return {
    psTotalByMarkets,
    printTotalByMarkets,
    grandTotalByMarkets,
    indexPsTotalByMarkets,
    indexPrintTotalByMarkets,
    indexGrandTotalByMarkets,
    indexPolyTotalByMarkets,
    polyByMarketsTotal,
  }
}

export const getDataWithChangedTotal = (
  data: IRevenue[],
  actualValueTypes: ActualsValueTypes[]
) => {
  const newData = [...data]

  const {
    psTotalByMarkets,
    printTotalByMarkets,
    grandTotalByMarkets,
    indexPrintTotalByMarkets,
    indexGrandTotalByMarkets,
    indexPsTotalByMarkets,
    indexPolyTotalByMarkets,
    polyByMarketsTotal,
  } = recomputeRowsTotal(data, actualValueTypes)

  if (indexPrintTotalByMarkets > -1) {
    newData[indexPrintTotalByMarkets] = printTotalByMarkets
  }

  if (indexPsTotalByMarkets > -1) {
    newData[indexPsTotalByMarkets] = psTotalByMarkets
  }

  if (indexPolyTotalByMarkets > -1) {
    newData[indexPolyTotalByMarkets] = polyByMarketsTotal
  }

  if (indexGrandTotalByMarkets > -1) {
    newData[indexGrandTotalByMarkets] = grandTotalByMarkets
  }

  return newData
}
